/**
 * @fileoverview Zod schemas for SendGrid API requests and responses.
 */

import { z } from '@hono/zod-openapi';

// ── Single Sends ──

export const SingleSendSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status: z.enum(['draft', 'scheduled', 'triggered']),
    send_at: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
    categories: z.array(z.string()).optional(),
    send_to: z.object({
      list_ids: z.array(z.string()).optional(),
      segment_ids: z.array(z.string()).optional(),
      all: z.boolean().optional(),
    }).optional(),
    email_config: z.object({
      subject: z.string().optional(),
      sender_id: z.number().optional(),
      html_content: z.string().optional(),
      plain_content: z.string().optional(),
    }).optional(),
  })
  .openapi('SingleSend');

export const SingleSendListResponseSchema = z
  .object({
    result: z.array(SingleSendSchema),
  })
  .openapi('SingleSendListResponse');

export const SingleSendStatsSchema = z
  .object({
    id: z.string().optional(),
    stats: z.object({
      requests: z.number().optional(),
      delivered: z.number().optional(),
      opens: z.number().optional(),
      unique_opens: z.number().optional(),
      clicks: z.number().optional(),
      unique_clicks: z.number().optional(),
      bounces: z.number().optional(),
      bounce_drops: z.number().optional(),
      invalid_emails: z.number().optional(),
      spam_reports: z.number().optional(),
      spam_report_drops: z.number().optional(),
      unsubscribes: z.number().optional(),
    }).optional(),
  })
  .openapi('SingleSendStats');

export const UpdateSingleSendSchema = z
  .object({
    name: z.string().optional(),
    categories: z.array(z.string()).optional(),
    send_at: z.string().optional(),
    email_config: z.object({
      subject: z.string().optional(),
      html_content: z.string().optional(),
      plain_content: z.string().optional(),
    }).optional(),
  })
  .openapi('UpdateSingleSend');

// ── Contacts ──

export const ContactSchema = z
  .object({
    id: z.string(),
    email: z.string().email(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    custom_fields: z.record(z.string(), z.any()).optional(),
    list_ids: z.array(z.string()).optional(),
  })
  .openapi('Contact');

export const ContactSearchRequestSchema = z
  .object({
    email: z.string().min(1).openapi({
      description: 'Email address or partial email to search for',
      example: 'user@example.com',
    }),
  })
  .openapi('ContactSearchRequest');

export const ContactSearchResponseSchema = z
  .object({
    result: z.array(ContactSchema),
    contact_count: z.number(),
  })
  .openapi('ContactSearchResponse');

export const UpdateContactSchema = z
  .object({
    email: z.string().email().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    custom_fields: z.record(z.string(), z.any()).optional(),
  })
  .openapi('UpdateContact');

export const SuppressionStatusSchema = z
  .object({
    bounces: z.array(z.record(z.string(), z.any())),
    unsubscribes: z.array(z.record(z.string(), z.any())),
    spamReports: z.array(z.record(z.string(), z.any())),
  })
  .openapi('SuppressionStatus');

export const EngagementMessageSchema = z
  .object({
    issueNumber: z.number(),
    subject: z.string(),
    openedAt: z.string().nullable().optional(),
    clickCount: z.number(),
  })
  .openapi('EngagementMessage');

export const FieldDefinitionSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    field_type: z.string(),
  })
  .openapi('FieldDefinition');

export const FieldDefinitionsResponseSchema = z
  .object({
    custom_fields: z.array(FieldDefinitionSchema).optional(),
    reserved_fields: z.array(FieldDefinitionSchema).optional(),
  })
  .openapi('FieldDefinitionsResponse');
